// src/web/PerceptualSkillDevelopment.jsx

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';
import PerceptualSkillDevelopmentFAQ from '../../components/faq/PerceptualSkillDevelopmentFAQ';

const activitiesCatalogPath = '/app/catalog?type=activity';

export default function PerceptualSkillDevelopment() {
  return (
    <main className="service-landing-page perceptual-skill-development-page">
      <Helmet>
        <title>Perceptual Skill Development | All-Voiced</title>
        <meta
          name="description"
          content="Develop auditory-perceptual voice evaluation skills through structured listening, comparison, feedback, reflection, and facilitated group learning."
        />
      </Helmet>

      <PageTracker pageName="PerceptualSkillDevelopment" />

      <section className="service-landing-hero">
        <span className="services-eyebrow">
          Perceptual Skill Development
        </span>

        <h1>
          Developing Expertise in Auditory-Perceptual Voice Evaluation
        </h1>

        <p>
          Structured listening, comparison, feedback, and calibration
          experiences for students, clinicians, educators, researchers, and
          professionals working with voice.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to={activitiesCatalogPath}
          >
            Explore activities
          </Link>
        </div>

        <div className="service-landing-panel">
          <span>Listening</span>
          <span className="service-landing-separator">•</span>
          <span>Comparison</span>
          <span className="service-landing-separator">•</span>
          <span>Feedback</span>
          <span className="service-landing-separator">•</span>
          <span>Calibration</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-intro">
        <span className="services-label">
          Why perceptual skill development?
        </span>

        <h2>
          Perceptual expertise develops through structured listening
        </h2>

        <p>
          Auditory-perceptual voice evaluation is not simply an innate
          ability or a one-time content-learning exercise. It develops
          through repeated exposure, active perceptual judgment, comparison,
          feedback, and reflection on agreement, disagreement, and perceptual
          references.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <AVIcon icon="listen" size={28} color="#209385" />

            <h3>Listening and judgment</h3>

            <p>
              Practice identifying, describing, and rating perceptual voice
              characteristics across diverse samples and listening contexts.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="repeat" size={28} color="#209385" />

            <h3>Comparison and discrimination</h3>

            <p>
              Refine perceptual distinctions through paired comparisons,
              ranking, categorization, and other comparative listening tasks.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="assessment" size={28} color="#209385" />

            <h3>Feedback, reflection, and calibration</h3>

            <p>
              Compare responses with expert, consensus, or structured reference
              data where available, reflect on agreement and disagreement, and
              progressively refine perceptual judgment.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Activity formats
        </span>

        <h2>
          Structured listening can take multiple forms
        </h2>

        <p>
          Available formats may include VAS rating activities, paired
          comparisons or 2AFC tasks, categorization, ranking and sorting,
          CAPE-V and CAPE-Vr-related activities, and other structured
          listening resources. Activities can be used independently from
          the Catalog or run with others through Live Sessions.
        </p>

        <p>
          Available formats evolve as new activities, resources, programs,
          and sessions are added. The app catalog remains the current source
          of truth for specific availability.
        </p>
      </section>

      <section className="service-landing-section service-landing-highlight">
        <span className="services-label">
          Participation formats
        </span>

        <h2>
          Independent practice and facilitated group learning
        </h2>

        <p>
          All-Voiced supports different participation formats. Some
          experiences can be completed independently, while others require
          synchronous participation and facilitation.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <AVIcon icon="user" size={28} color="#209385" />

            <h3>Independent practice</h3>

            <p>
              Some catalog entries can be launched and completed at the
              user’s own pace, depending on access conditions and activity
              design.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="team" size={28} color="#209385" />

            <h3>Facilitated group learning</h3>

            <p>
              Facilitators start Live Sessions for synchronous group use,
              and participants join them in a shared classroom, workshop,
              or online setting.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Contexts
        </span>

        <h2>
          Relevant across learning and professional contexts
        </h2>

        <p>
          Perceptual skill development can support learners and professionals
          with different objectives, backgrounds, and levels of experience.
          Suitability, feedback models, and access conditions vary across
          activities.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Students</h3>
            <p>
              Build perceptual references and practice structured judgment
              across voice samples and task types.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Clinicians</h3>
            <p>
              Refine structured listening and consistency within
              professional voice evaluation contexts.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Educators</h3>
            <p>
              Use activities for classroom practice, guided discussion,
              facilitated group learning, or course support.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Researchers</h3>
            <p>
              Explore perceptual tasks, evaluator workflows, and listening
              paradigms relevant to research design and perceptual data
              generation.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Voice professionals</h3>
            <p>
              Strengthen listening across pedagogical, performance,
              coaching, or advisory contexts.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Access
        </span>

        <h2>
          Flexible ways to access perceptual activities
        </h2>

        <p>
          Many All-Voiced activities are currently available free of charge.
          Other entries may require registration, eligibility, institutional
          access, or payment. Specialized resources, facilitated sessions,
          programs, and licensed materials may use different access models.
          Current access conditions are shown in each catalog entry.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--secondary"
            to="/access-and-pricing"
          >
            Learn about access and pricing
          </Link>
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Related service line
        </span>

        <h2>
          From individual practice to institutional implementation
        </h2>

        <p>
          Perceptual Skill Development focuses on activities and experiences
          for individuals and groups. Institutional Programs configure these
          and other capabilities within courses, cohorts, assessment models,
          learning analytics, educational research, or broader institutional
          implementation.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>Institutional Programs</h3>

            <p>
              Explore structured educational implementation for courses,
              cohorts, assessment, analytics, and educational research.
            </p>
          </div>

          <Link
            className="web-button web-button--secondary"
            to="/institutional-programs"
          >
            Explore institutional programs
          </Link>
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Catalog
        </span>

        <h2>
          Explore available activities and resources
        </h2>

        <p>
          Browse the All-Voiced app catalog to discover current activities,
          programs, and resources for auditory-perceptual voice
          evaluation.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>All-Voiced app catalog</h3>

            <p>
              Discover available entries, review access conditions, and
              launch the experiences that fit your context.
            </p>
          </div>

          <Link
            className="web-button web-button--primary"
            to={activitiesCatalogPath}
          >
            Explore activities
          </Link>
        </div>
      </section>

      <PerceptualSkillDevelopmentFAQ />

      <section className="services-final-cta">
        <h2>
          Develop perceptual expertise through structured listening
        </h2>

        <p>
          Explore current activities and resources designed to support
          auditory-perceptual voice evaluation skill development.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to={activitiesCatalogPath}
          >
            Explore activities
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="PerceptualSkillDevelopment" />
    </main>
  );
}
